import React, { useState, useEffect } from 'react';
import { Bell, Smartphone, Settings, AlertCircle, CheckCircle } from 'lucide-react';
import TelegramConnect from './TelegramConnect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const NotificationSettings = ({ userId }) => {
  const [telegramStatus, setTelegramStatus] = useState({
    connected: false,
    chat_id: null,
    connected_at: null
  });
  const [globalNotifications, setGlobalNotifications] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      loadTelegramStatus();
      loadUserPreferences();
    }
  }, [userId]);

  const loadTelegramStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/telegram/status/${userId}`);
      const data = await response.json();

      if (response.ok) {
        setTelegramStatus(data);
      } else {
        console.error('Failed to load Telegram status:', data);
      }
    } catch (error) {
      console.error('Error loading Telegram status:', error);
      setError('Failed to load notification settings');
    }
  };

  const loadUserPreferences = async () => {
    try {
      // This would need to be implemented in the backend
      // For now, we'll assume notifications are enabled by default
      setGlobalNotifications(true);
      setLoading(false);
    } catch (error) {
      console.error('Error loading user preferences:', error);
      setError('Failed to load user preferences');
      setLoading(false);
    }
  };

  const handleTelegramConnect = () => {
    // This will be handled by the TelegramConnect component
    loadTelegramStatus();
  };

  const handleTelegramDisconnect = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/telegram/disconnect/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setTelegramStatus({
          connected: false,
          chat_id: null,
          connected_at: null
        });
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to disconnect Telegram');
      }
    } catch (error) {
      console.error('Error disconnecting Telegram:', error);
      setError('Failed to disconnect Telegram');
    } finally {
      setLoading(false);
    }
  };

  const toggleGlobalNotifications = async () => {
    try {
      setLoading(true);
      // This would need to be implemented in the backend
      // For now, just update the local state
      setGlobalNotifications(!globalNotifications);
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      setError('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !telegramStatus.connected) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Bell className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Paramètres de notification</h3>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Bell className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-medium text-gray-900">Paramètres de notification</h3>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-sm text-red-600 underline hover:text-red-800"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Global Notifications Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <Settings className="h-5 w-5 text-gray-600" />
            <div>
              <h4 className="font-medium text-gray-900">Notifications générales</h4>
              <p className="text-sm text-gray-600">
                Activer ou désactiver toutes les notifications
              </p>
            </div>
          </div>
          <button
            onClick={toggleGlobalNotifications}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              globalNotifications ? 'bg-blue-600' : 'bg-gray-200'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                globalNotifications ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Telegram Integration */}
        <div className="border-t pt-6">
          <div className="flex items-center space-x-3 mb-4">
            <Smartphone className="h-5 w-5 text-blue-600" />
            <h4 className="font-medium text-gray-900">Telegram</h4>
          </div>

          {telegramStatus.connected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Telegram connecté</p>
                    <p className="text-sm text-green-700">
                      Connecté le {new Date(telegramStatus.connected_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    {telegramStatus.chat_id && (
                      <p className="text-xs text-green-600 font-mono">
                        Chat ID: {telegramStatus.chat_id}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleTelegramDisconnect}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-200 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Déconnecter
                </button>
              </div>

              <div className="text-sm text-gray-600">
                <p>✅ Vous recevrez des notifications Telegram pour :</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Nouvelles annonces dans vos recherches sauvegardées</li>
                  <li>Mises à jour importantes de l'application</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600">
                Connectez votre compte Telegram pour recevoir des notifications instantanées
                lorsque de nouvelles annonces correspondent à vos recherches.
              </p>

              <TelegramConnect
                userId={userId}
                onConnect={handleTelegramConnect}
                onError={setError}
              />

              <div className="text-sm text-gray-500">
                <p className="font-medium mb-2">Avantages des notifications Telegram :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Notifications instantanées sur votre téléphone</li>
                  <li>Aperçu des nouvelles annonces avec images</li>
                  <li>Liens directs vers les annonces Avito</li>
                  <li>Aucune limite de notifications</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Future notification channels */}
        <div className="border-t pt-6">
          <h4 className="font-medium text-gray-900 mb-4">Autres canaux (bientôt disponibles)</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg opacity-50">
              <div className="flex items-center space-x-3">
                <div className="h-5 w-5 bg-gray-400 rounded"></div>
                <span className="text-gray-600">Notifications email</span>
              </div>
              <span className="text-sm text-gray-500">Bientôt</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg opacity-50">
              <div className="flex items-center space-x-3">
                <div className="h-5 w-5 bg-gray-400 rounded"></div>
                <span className="text-gray-600">Notifications push</span>
              </div>
              <span className="text-sm text-gray-500">Bientôt</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;